import {
  FETCH_CART_MOMENTS,
  ADD_CART_MOMENT,
  FETCH_CART_SELECTIONS,
  SELECT_CART_MOMENTS,
  UNSELECT_CART_MOMENTS,
  FETCH_PRINTS,
  SELECT_PRINTS,
  UNSELECT_PRINTS,
  REMOVE_CART_MOMENT,
  SET_PURCHASE_REQUEST,
  FETCH_PURCHASES,
  SET_PURCHASED,
  NEW_ORDER,
  FETCH_PURCHASED_MOMENT_IDS,
  LOGOUT,
  FETCH_PURCHASE_CALCULATIONS,
  ADD_PURCHASE,
  SET_AS_MOBILE_CART,
  SHOW_MOBILE_CART_PREMIUM_GALLERY,
  SHOW_CHECKOUT_FORM,
  CLEAR_PURCHASE_REQUEST,
  SET_PURCHASING_LOADING,
  APPLY_DISCOUNT_COUPON,
  SET_PRINT_SIZES,
  GET_SELECTED_PRINT_SIZES,
  CLEAR_SELECTED_PRINT_SIZES,
  RE_KNOCKOUT_MOMENT,
  PREMIUM_GALLERY_LOADED,
  REVIEW_CART
} from 'shared/actions/types';

import produce from 'immer';

const initialState = {
  moments: {
    byId: {},
    allIds: []
  },
  selectedMomentIds: [],
  selectedPrintMomentIds: [],
  purchaseRequest: {
    amount: null,
    currency: null,
    moment_count: null,
    unlock_all: null,
    print_count: null
  },
  // todo: review how prints array is used
  prints: {
    byId: {},
    byMomentId: {}
  },
  purchased: false,
  purchases: [],
  purchasedMomentIds: [],
  calculations: {
    by_quantity: [],
    pricing_model: null,
    currency: null,
    discounts: null
  },
  isMobileCart: false,
  showPremiumGallery: false,
  checkOutVisible: false,
  purchaseLoading: false,
  selectedPrintSizes: [],
  reKnockedOutMoments: [],
  premiumGalleryLoaded: false,
  onCartReview: false
}


const fetchResource = (resource, payload, draft) => {
  draft[resource].byId = {};
  draft[resource].allIds = [];

  if (Array.isArray(payload)) {
    payload.forEach(record => {
      draft[resource].byId[record.id] = record;
      draft[resource].allIds.push(record.id);
    });
  } else {
    draft[resource].byId[payload.id] = payload;
    draft[resource].allIds.push(payload.id);
  }

  draft[resource].allIds = draft[resource].allIds.filter((value, index, self) => {
    return self.indexOf(value) === index;
  })
    .sort((a, b) => a - b);
}

export default function (state = initialState, action) {
  switch (action.type) {
    case FETCH_CART_MOMENTS:
      return produce(state, draft => {
        // clear cart moments, pagination isn't used atm
        draft.moments.allIds = [];
        draft.moments.byId = {};

        fetchResource('moments', action.payload, draft);
      });

    case ADD_CART_MOMENT:
      return produce(state, draft => {

        if (!draft['moments'].byId[action.payload.id]) {
          draft['moments'].byId[action.payload.id] = action.payload;
          draft['moments'].allIds.push(action.payload.id);
        }
      });

    case FETCH_CART_SELECTIONS:
      return produce(state, draft => {
        draft.selectedMomentIds = action.payload
          .filter(s => {
            let m = draft.moments.byId[s.moment_id];
            if (m) {
              return !m.purchased;
            }
            return false;
          })
          .map(s => s.moment_id);

        draft.selectedPrintMomentIds = [];
        for (let selection of action.payload.filter(s => s.print_enabled)) {
          for (let count = 0; count < selection.print_count; count++) {
            draft.selectedPrintMomentIds.push(selection.moment_id);
          }
        }
      });

    case SELECT_CART_MOMENTS:
      return produce(state, draft => {
        action.payload.forEach(momentId => {
          if (draft.selectedMomentIds.indexOf(momentId) === -1) {
            draft.selectedMomentIds.push(momentId);
          }
        });
      });

    case UNSELECT_CART_MOMENTS:
      return produce(state, draft => {
        const momentIds = action.payload;

        // unselect digitals
        draft.selectedMomentIds = draft.selectedMomentIds.filter(momentId => {
          return !momentIds.includes(momentId);
        });

        // unselect prints
        draft.selectedPrintMomentIds = draft.selectedPrintMomentIds.filter(momentId => {
          return !momentIds.includes(momentId);
        });

        // remove from selectedPrintSizes (clear print sizes)
        draft.selectedPrintSizes = draft.selectedPrintSizes.filter(size => {
          return !momentIds.includes(size.moment_id);
        });
      });

    case REMOVE_CART_MOMENT:
      return produce(state, draft => {
        draft.selectedMomentIds = draft.selectedMomentIds.filter(id => {
          return id !== action.payload;
        });

        draft.selectedPrintMomentIds = draft.selectedPrintMomentIds.filter(id => {
          return id !== action.payload;
        });

        draft.selectedPrintSizes = draft.selectedPrintSizes.filter(print => {
          return print.moment_id !== action.payload;
        });

        draft.moments.allIds = draft.moments.allIds.filter(id => {
          return id !== action.payload;
        });
      });

    case FETCH_PRINTS:
      return produce(state, draft => {
        for (let print of action.payload) {
          draft.prints.byId[print.id] = print;
          draft.prints.byMomentId[print.moment_id] = print;
        }
      });

    case SELECT_PRINTS:
      return produce(state, draft => {
        action.payload.forEach(momentId => {
          draft.selectedPrintMomentIds.push(momentId);
        });
      });

    case UNSELECT_PRINTS:
      return produce(state, draft => {
        let momentIds = action.payload.momentIds;

        // If clearing all prints, filter them out and return early
        if (action.payload.clearAll) {
          draft.selectedPrintMomentIds = state.selectedPrintMomentIds.filter(id => {
            return !momentIds.includes(id);
          });

          return
        }

        for (let momentId of momentIds) {
          let index = draft.selectedPrintMomentIds.indexOf(momentId);
          if (index > -1) {
            draft.selectedPrintMomentIds.splice(index, 1);
          }
        }
      });

    case SET_PURCHASE_REQUEST:
      return produce(state, draft => {
        draft.purchaseRequest = action.payload;
      });

    case CLEAR_PURCHASE_REQUEST:
      return produce(state, draft => {
        draft.purchaseRequest = initialState.purchaseRequest;
      });

    case FETCH_PURCHASES:
      return produce(state, draft => {
        // sort chronologically
        draft.purchases = action.payload.sort((a, b) => a.id > b.id ? 1 : -1);
      });

    case LOGOUT:
      return produce(state, draft => {
        draft.moments = {
          byId: {},
          allIds: []
        };
        draft.selectedMomentIds = [];
        draft.selectedPrintMomentIds = [];
        draft.purchaseRequest = {
          amount: null,
          currency: null,
          moment_count: null,
          unlock_all: null,
          print_count: null
        };
        draft.prints = {
          byId: {},
          byMomentId: {}
        }
        draft.purchased = false;
        draft.purchases = [];
        draft.purchasedMomentIds = [];
        draft.calculations = {
          by_quantity: [],
          pricing_model: null,
          currency: null,
          discounts: null
        };
        draft.isMobileCart = false;
        draft.showPremiumGallery = false;
        draft.checkOutVisible = false;
        draft.purchaseLoading = false;
        draft.selectedPrintSizes = [];
        draft.reKnockedOutMoments = [];
        draft.premiumGalleryLoaded = false;
      });

    case SET_PURCHASED:
      return produce(state, draft => {
        draft.purchased = true;

        let momentIds = action.payload;
        if (momentIds) {
          for (let id of momentIds) {
            if (draft.moments.allIds.includes(id)) {
              draft.moments.byId[id].purchased = true;
            }
          }

          draft.selectedMomentIds = draft.selectedMomentIds.filter(id => momentIds.indexOf(id) === -1);
          draft.selectedPrintMomentIds = draft.selectedPrintMomentIds.filter(id => momentIds.indexOf(id) === -1);
          draft.purchasedMomentIds = draft.purchasedMomentIds.concat(momentIds);
        }
      });

    case ADD_PURCHASE:
      return produce(state, draft => {
        let { purchase, momentIds } = action.payload;

        draft.purchases.push(purchase);
        draft.purchased = true;

        if (momentIds) {
          for (let id of momentIds) {
            if (draft.moments.allIds.includes(id)) {
              draft.moments.byId[id].purchased = true;
            }
          }
        }
      });

    case NEW_ORDER:
      return produce(state, draft => {
        draft.purchaseRequest = {
          amount: null,
          currency: null,
          moment_count: null,
          unlock_all: null
        }
        draft.prints = {
          byId: {},
          byMomentId: {}
        }
        draft.purchased = false;
      });

    case FETCH_PURCHASED_MOMENT_IDS:
      return produce(state, draft => {
        draft.purchasedMomentIds = action.payload;
      });

    case FETCH_PURCHASE_CALCULATIONS:
      return produce(state, draft => {
        draft.calculations = action.payload;
      });

    case SET_AS_MOBILE_CART:
      return produce(state, draft => {
        draft.isMobileCart = action.payload;
      });

    case SHOW_MOBILE_CART_PREMIUM_GALLERY:
      return produce(state, draft => {
        draft.showPremiumGallery = action.payload;
      });

    case PREMIUM_GALLERY_LOADED:
      return produce(state, draft => {
        draft.premiumGalleryLoaded = action.payload;
      });

    case SHOW_CHECKOUT_FORM:
      return produce(state, draft => {
        draft.checkOutVisible = action.payload;
      });

    case SET_PURCHASING_LOADING:
      return produce(state, draft => {
        draft.purchaseLoading = action.payload;
      });

    case SET_PURCHASING_LOADING:
      return produce(state, draft => {
        draft.purchaseLoading = action.payload;
      });

    case APPLY_DISCOUNT_COUPON:
      return produce(state, draft => {
        draft.discountCouponApplied = action.payload;
      });

    case SET_PRINT_SIZES:
    case GET_SELECTED_PRINT_SIZES:
      return produce(state, draft => {
        draft.selectedPrintSizes = action.payload;
      });
    case CLEAR_SELECTED_PRINT_SIZES:
      return produce(state, draft => {
        let selectedPrintSizes = Object.assign([], state.selectedPrintSizes);
        for (let selected of state.selectedPrintSizes) {
          let selectedIndex = selectedPrintSizes.indexOf(selected);
          if (selected.story_id === action.payload.story_id &&
            selected.moment_id === action.payload.moment_id) {
            selectedPrintSizes.splice(selectedIndex, 1);
          }
        }
        draft.selectedPrintSizes = selectedPrintSizes;
      });

    case RE_KNOCKOUT_MOMENT:
      return produce(state, draft => {
        var moments = Object.values(state.moments.byId);
        // for (var moment of action.payload) {
        //   var oldMoment = moments.find(x => x.orig_moment_id === moment.orig_moment_id);
        //   var oldMomentIndex = moments.indexOf(oldMoment);
        //   moment.index = oldMomentIndex;
        //   moment.position = oldMomentIndex + 1;
        //   moments.splice(oldMomentIndex, 1);
        //   moments.push();
        // }
        fetchResource('moments', moments, draft);
        fetchResource('reKnockedOutMoments', action.payload, draft);
      });
    case REVIEW_CART:
      return produce(state, draft => {
        draft.onCartReview = action.payload;
      });

    default:
      return state;
  }
}
